import {PagedRequestDTO} from '../../common/models/PagedRequestDTO';
import {TableControl} from '../../common/models/TableControl';
import {createFieldMap, getSortDirection} from '../../common/util/Util';
import {PagedFilterDTO} from '../../common/models/PagedFilterDTO';
import {EntityTable} from '../../common/types/entity-table.enum';

export class EntRequestBuilder {

  // By Table Control:

  public static getListOfProductsPagedRequestDTO(tableControl: TableControl): PagedRequestDTO {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblProduct;
    pagedRequestDTO.pageNumber = tableControl.pageNumber;
    pagedRequestDTO.pageSize = tableControl.pageSize;
    pagedRequestDTO.sortField = tableControl.sortField;
    pagedRequestDTO.sortDirection = tableControl.sortDirection;

    // Note: 'filterField' to control table filter data by 'filterValue'
    if (tableControl.filterField) {
      pagedRequestDTO.fieldMap = createFieldMap(
        tableControl.filterField,
        tableControl.filterValue
      );
    }
    else {
      pagedRequestDTO.fieldMap = null;
    }
    return pagedRequestDTO;
  }

  public static getListOfBenefitsPagedRequestDTO(tableControl: TableControl): PagedRequestDTO {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblBenefit;
    pagedRequestDTO.pageNumber = tableControl.pageNumber;
    pagedRequestDTO.pageSize = tableControl.pageSize;
    pagedRequestDTO.sortField = tableControl.sortField;
    pagedRequestDTO.sortDirection = tableControl.sortDirection;

    // Note: 'filterField' to control table filter data by 'filterValue'
    if (tableControl.filterField) {
      pagedRequestDTO.fieldMap = createFieldMap(
        tableControl.filterField,
        tableControl.filterValue
      );
    } else {
      pagedRequestDTO.fieldMap = null;
    }
    return pagedRequestDTO;
  }

  public static getListOfLimitsPagedRequestDTO(tableControl: TableControl): PagedRequestDTO {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblLimit;
    pagedRequestDTO.pageNumber = tableControl.pageNumber;
    pagedRequestDTO.pageSize = tableControl.pageSize;
    pagedRequestDTO.sortField = tableControl.sortField;
    pagedRequestDTO.sortDirection = tableControl.sortDirection;

    // Note: 'filterField' to control table filter data by 'filterValue'
    if (tableControl.filterField) {
      pagedRequestDTO.fieldMap = createFieldMap(
        tableControl.filterField,
        tableControl.filterValue
      );
    } else {
      pagedRequestDTO.fieldMap = null;
    }
    return pagedRequestDTO;
  }

  public static getPagedRequestDTO(tableControl: TableControl, entityClass: string): PagedRequestDTO {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = entityClass;
    pagedRequestDTO.pageNumber = tableControl.pageNumber;
    pagedRequestDTO.pageSize = tableControl.pageSize;
    pagedRequestDTO.sortField = tableControl.sortField;
    pagedRequestDTO.sortDirection = tableControl.sortDirection;
    // Note: 'filterField' to control table filter data by 'filterValue'
    if (tableControl.filterField) {
      pagedRequestDTO.fieldMap = createFieldMap(
        tableControl.filterField,
        tableControl.filterValue
      );
    } else {
      pagedRequestDTO.fieldMap = null;
    }
    return pagedRequestDTO;
  }

  public static getProviderNetworkPagedRequestDTO(tableControl: TableControl): PagedRequestDTO {

    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblProviderNetwork;
    pagedRequestDTO.pageNumber = tableControl.pageNumber;
    pagedRequestDTO.pageSize = tableControl.pageSize;
    pagedRequestDTO.sortField = tableControl.sortField;
    pagedRequestDTO.sortDirection = tableControl.sortDirection;

    // Note: 'filterField' to control table filter data by 'filterValue'
    if (tableControl.filterField) {
      pagedRequestDTO.fieldMap = createFieldMap(
        tableControl.filterField,
        tableControl.filterValue
      );
    } else {
      pagedRequestDTO.fieldMap = null;
    }
    return pagedRequestDTO;
  }

  // By Value:
  public static getFilteredListOfBenefitsPagedRequestDTO(value: string) {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblBenefit;
    pagedRequestDTO.pageNumber = 0;
    pagedRequestDTO.pageSize = 50;
    pagedRequestDTO.sortField = 'name';
    pagedRequestDTO.sortDirection = 'ASCENDING';
    pagedRequestDTO.fieldMap = createFieldMap('name', value);
    return pagedRequestDTO;
  }

  // Act as default base method to get PagedRequestDTO
  // Filtering on Single || Multiple Columns Applied on
  // Component Level

  public static getTblBenefitsPagedRequestDTO({
                                                pageNumber = 0,
                                                pageSize = 50,
                                                sortField = 'name',
                                                sortDirection = 'ASCENDING',
                                                filterField = 'name',
                                                filterValue = null
                                              }) {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblBenefit;
    pagedRequestDTO.pageNumber = pageNumber;
    pagedRequestDTO.pageSize = pageSize;
    pagedRequestDTO.sortField = sortField;
    pagedRequestDTO.sortDirection = sortDirection;
    pagedRequestDTO.fieldMap = createFieldMap(filterField, filterValue);
    return pagedRequestDTO;
  }

  public static getFilteredListOfLimitsPagedRequestDTO(value: string): PagedRequestDTO {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblLimit;
    pagedRequestDTO.pageNumber = 0;
    pagedRequestDTO.pageSize = 50;
    pagedRequestDTO.sortField = 'name';
    pagedRequestDTO.sortDirection = 'ASCENDING';
    pagedRequestDTO.fieldMap = createFieldMap('name', value);
    return pagedRequestDTO;
  }

  public static getFilteredListOfPayersPagedRequestDTO(value: string): PagedRequestDTO {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblPayer;
    pagedRequestDTO.pageNumber = 0;
    pagedRequestDTO.pageSize = 50;
    pagedRequestDTO.sortField = 'name';
    pagedRequestDTO.sortDirection = 'ASCENDING';
    pagedRequestDTO.fieldMap = createFieldMap('name', value);
    return pagedRequestDTO;
  }

  public static getFilteredListOfProviderNetworkPagedRequestDTO(value: string): PagedRequestDTO {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblProviderNetwork;
    pagedRequestDTO.pageNumber = 0;
    pagedRequestDTO.pageSize = 50;
    pagedRequestDTO.sortField = 'name';
    pagedRequestDTO.sortDirection = 'ASCENDING';
    pagedRequestDTO.fieldMap = createFieldMap('name', value);
    return pagedRequestDTO;
  }

  public static getListOfClientBankAccounts(payerIdList: string, name: string, processingClassId: number, processingSubClassId: number, productId: number): PagedRequestDTO {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblBankAccount;
    pagedRequestDTO.pageNumber = 0;
    pagedRequestDTO.pageSize = 100;
    pagedRequestDTO.sortField = 'name';
    pagedRequestDTO.sortDirection = 'ASCENDING';
    pagedRequestDTO.fieldMap = {};

    const pagedFilterDTO1 = new PagedFilterDTO();
    pagedFilterDTO1.name = 'clientAccount';
    pagedFilterDTO1.value = '1';
    pagedRequestDTO.fieldMap[pagedFilterDTO1.name] = pagedFilterDTO1;

    if (payerIdList != null) {
      const pagedFilterDTO2 = new PagedFilterDTO();
      pagedFilterDTO2.name = 'payerByPayerId.payerId';
      pagedFilterDTO2.operator = 'in';
      pagedFilterDTO2.value = payerIdList;
      pagedRequestDTO.fieldMap['payerByPayerId'] = pagedFilterDTO2;
    }

    if (name != null) {
      const pagedFilterDTO3 = new PagedFilterDTO();
      pagedFilterDTO3.name = 'paymentDefaultByPaymentDefaultId.name';
      pagedFilterDTO3.operator = '==';
      pagedFilterDTO3.value = name;
      pagedRequestDTO.fieldMap['paymentDefaultByPaymentDefaultId_1'] = pagedFilterDTO3;
    }

    if (processingClassId != null) {
      const pagedFilterDTO4 = new PagedFilterDTO();
      pagedFilterDTO4.name = 'paymentDefaultByPaymentDefaultId.processingClassByProcessingClassId.processingClassId';
      pagedFilterDTO4.value = String(processingClassId);
      pagedRequestDTO.fieldMap['paymentDefaultByPaymentDefaultId_2'] = pagedFilterDTO4;
    }

    if (processingSubClassId != null) {
      const pagedFilterDTO5 = new PagedFilterDTO();
      pagedFilterDTO5.name = 'paymentDefaultByPaymentDefaultId.processingSubClassByProcessingSubClassId.processingSubClassId';
      pagedFilterDTO5.value = String(processingSubClassId);
      pagedRequestDTO.fieldMap['paymentDefaultByPaymentDefaultId_3'] = pagedFilterDTO5;
    }

    if (productId != null) {
      const pagedFilterDTO6 = new PagedFilterDTO();
      pagedFilterDTO6.name = 'paymentDefaultByPaymentDefaultId.productByProductId.productId';
      pagedFilterDTO6.value = String(productId);
      pagedRequestDTO.fieldMap['paymentDefaultByPaymentDefaultId_4'] = pagedFilterDTO6;
    }

    return pagedRequestDTO;
  }

  public static getTableControls(length = 100,
                                 pageSize = 10,
                                 pageNumber = 0,
                                 pageSizeOptions = [5, 10, 25, 100]) {
    return {
      length,
      pageSize,
      pageNumber,
      pageSizeOptions
    };
  }

  public static buildBasicPagedRequestDTO(entityClass: string, tableControl: TableControl): PagedRequestDTO {
    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = entityClass;
    pagedRequestDTO.pageNumber = tableControl.pageNumber;
    pagedRequestDTO.pageSize = tableControl.pageSize;
    pagedRequestDTO.sortField = tableControl.sortField;
    pagedRequestDTO.sortDirection = getSortDirection(tableControl.sortDirection);
    pagedRequestDTO.fieldMap = {};

    return pagedRequestDTO;
  }
}
